// src/pages/documentation/TpvAgoraDocumentation.jsx

import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faChevronDown,
    faChevronUp,
    faCopy,
    faCheck,
    faExclamationTriangle,
    faInfoCircle,
    faCog,
    faQrcode,
    faKey,
    faGlobe,
    faCode,
    faRocket,
    faShieldAlt,
    faChartLine,
    faTools,
    faPhone,
    faEnvelope
} from '@fortawesome/free-solid-svg-icons';

const TpvAgoraDocumentation = () => {
    const [expandedSections, setExpandedSections] = useState({
        introduction: true,
        requirements: true,
        'petitprime-setup': true,
        'agora-setup': true,
        testing: true,
        workflow: true,
        support: true
    });
    const [copiedItems, setCopiedItems] = useState({});
    const [allExpanded, setAllExpanded] = useState(true);

    const toggleSection = (sectionId) => {
        setExpandedSections(prev => ({
            ...prev,
            [sectionId]: !prev[sectionId]
        }));
    };

    const toggleAllSections = () => {
        const newExpanded = !allExpanded;
        setAllExpanded(newExpanded);
        const newSections = {};
        sections.forEach(section => {
            newSections[section.id] = newExpanded;
        });
        setExpandedSections(newSections);
    };

    const copyToClipboard = async (text, itemId) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopiedItems(prev => ({ ...prev, [itemId]: true }));
            setTimeout(() => {
                setCopiedItems(prev => ({ ...prev, [itemId]: false }));
            }, 2000);
        } catch (err) {
            console.error('Error copying to clipboard:', err);
        }
    };

    const sections = [
        {
            id: 'introduction',
            title: '¿Qué es el TPV con Agora?',
            icon: faRocket,
            content: (
                <div className="space-y-4">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <h4 className="font-semibold text-blue-800 mb-2">¿Qué vas a conseguir?</h4>
                        <ul className="space-y-2 text-blue-700">
                            <li className="flex items-center">
                                <FontAwesomeIcon icon={faCheck} className="mr-2 text-green-600" />
                                Los clientes acumulan puntos automáticamente al escanear su QR
                            </li>
                            <li className="flex items-center">
                                <FontAwesomeIcon icon={faCheck} className="mr-2 text-green-600" />
                                No necesitas hacer nada manualmente - todo es automático
                            </li>
                            <li className="flex items-center">
                                <FontAwesomeIcon icon={faCheck} className="mr-2 text-green-600" />
                                Funciona con tu software Agora existente
                            </li>
                            <li className="flex items-center">
                                <FontAwesomeIcon icon={faCheck} className="mr-2 text-green-600" />
                                Los puntos se suman instantáneamente a la cuenta del cliente
                            </li>
                        </ul>
                    </div>
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <h4 className="font-semibold text-green-800 mb-2">¿Cómo funciona?</h4>
                        <ol className="space-y-2 text-green-700">
                            <li>1. El cliente presenta su QR en el TPV</li>
                            <li>2. Agora escanea el QR y procesa la venta</li>
                            <li>3. Los puntos se acumulan automáticamente</li>
                            <li>4. El cliente recibe notificación de sus nuevos puntos</li>
                        </ol>
                    </div>
                </div>
            )
        },
        {
            id: 'requirements',
            title: '¿Qué necesitas?',
            icon: faShieldAlt,
            content: (
                <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="bg-white border border-gray-200 rounded-lg p-4">
                            <h4 className="font-semibold text-gray-800 mb-3 flex items-center">
                                <FontAwesomeIcon icon={faQrcode} className="mr-2 text-green-600" />
                                Tu TPV Agora
                            </h4>
                            <ul className="space-y-2 text-sm text-gray-600">
                                <li>• Software Agora v8.5.0 o superior</li>
                                <li>• Conexión a internet estable</li>
                                <li>• Acceso de administrador al TPV</li>
                                <li>• Escáner de códigos QR configurado</li>
                            </ul>
                        </div>
                        <div className="bg-white border border-gray-200 rounded-lg p-4">
                            <h4 className="font-semibold text-gray-800 mb-3 flex items-center">
                                <FontAwesomeIcon icon={faInfoCircle} className="mr-2 text-purple-600" />
                                Información que necesitarás
                            </h4>
                            <ul className="space-y-2 text-sm text-gray-600">
                                <li>• Nombre del terminal (ej: "Caja Principal")</li>
                                <li>• Ubicación del TPV (ej: "Entrada principal")</li>
                                <li>• Dirección IP del TPV (opcional)</li>
                                <li>• Descripción del terminal</li>
                            </ul>
                        </div>
                    </div>
                </div>
            )
        },
        {
            id: 'petitprime-setup',
            title: 'Paso 1: Configurar en PetitPrime',
            icon: faCog,
            content: (
                <div className="space-y-6">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <h4 className="font-semibold text-blue-800 mb-2">Crear Configuración TPV</h4>
                        <p className="text-blue-700 text-sm mb-3">
                            Primero necesitas crear la configuración TPV en tu panel de PetitPrime.
                        </p>
                        <div className="bg-white p-3 rounded border">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm font-medium text-gray-800">📍 Ubicación en el panel:</p>
                                    <p className="text-sm text-gray-600">Tiendas → [Tu Tienda] → TPV</p>
                                </div>
                                <button
                                    onClick={() => copyToClipboard('Tiendas → [Tu Tienda] → TPV', 'navigation-path')}
                                    className="ml-2 text-blue-600 hover:text-blue-800"
                                >
                                    <FontAwesomeIcon icon={copiedItems['navigation-path'] ? faCheck : faCopy} />
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <h4 className="font-semibold text-green-800 mb-3">¿Qué información necesitas llenar?</h4>
                        <div className="space-y-3">
                            <div className="bg-white p-3 rounded border">
                                <h5 className="font-medium text-gray-800 mb-2">📝 Campos del formulario:</h5>
                                <ul className="space-y-1 text-sm text-gray-600">
                                    <li>• <strong>Nombre:</strong> "TPV Principal - Caja 1"</li>
                                    <li>• <strong>Descripción:</strong> "Terminal principal en la entrada"</li>
                                    <li>• <strong>Tipo de Software:</strong> "Agora Software"</li>
                                    <li>• <strong>IP del TPV:</strong> "192.168.1.100" (opcional)</li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                        <h4 className="font-semibold text-yellow-800 mb-2">⚠️ Importante</h4>
                        <p className="text-yellow-700 text-sm mb-2">
                            Después de crear la configuración, el sistema te dará una <strong>API Key</strong> y una <strong>URL de Webhook</strong>.
                        </p>
                        <p className="text-yellow-700 text-sm">
                            <strong>Guarda esta información</strong> - la necesitarás para configurar Agora.
                        </p>
                    </div>
                </div>
            )
        },
        {
            id: 'agora-setup',
            title: 'Paso 2: Configurar en Agora',
            icon: faQrcode,
            content: (
                <div className="space-y-6">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <h4 className="font-semibold text-blue-800 mb-3">Acceder a Configuración de Integraciones</h4>
                        <ol className="space-y-2 text-blue-700">
                            <li>1. Iniciar sesión en Agora como administrador</li>
                            <li>2. Ir a: <code className="bg-blue-100 px-2 py-1 rounded">Configuración → Integraciones → API Externa</code></li>
                            <li>3. Crear nueva integración con PetitPrime</li>
                        </ol>
                    </div>

                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <h4 className="font-semibold text-green-800 mb-3">Configurar Webhook</h4>
                        <div className="space-y-3">
                            <div className="bg-white p-3 rounded border">
                                <h5 className="font-medium text-gray-800 mb-2">📋 Información que necesitas:</h5>
                                <p className="text-sm text-gray-600 mb-2">
                                    Usa la <strong>URL de Webhook</strong> que obtuviste en PetitPrime:
                                </p>
                                <div className="bg-gray-100 p-2 rounded">
                                    <code className="text-sm text-gray-800">
                                        https://api.petitprime.com/api/webhooks/agora?token=pp_agora_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx
                                    </code>
                                </div>
                            </div>

                            <div className="bg-white p-3 rounded border">
                                <h5 className="font-medium text-gray-800 mb-2">⚙️ Configuración en Agora:</h5>
                                <ul className="space-y-1 text-sm text-gray-600">
                                    <li>• <strong>Método HTTP:</strong> POST</li>
                                    <li>• <strong>Timeout:</strong> 120 segundos</li>
                                    <li>• <strong>Reintentos:</strong> 3</li>
                                    <li>• <strong>Headers:</strong> Content-Type: application/json</li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                        <h4 className="font-semibold text-purple-800 mb-3">Configurar Escáner de QR</h4>
                        <div className="space-y-3">
                            <div className="bg-white p-3 rounded border">
                                <h5 className="font-medium text-gray-800 mb-2">1. Habilitar Escáner:</h5>
                                <ol className="space-y-1 text-sm text-gray-600">
                                    <li>• Ir a: <code className="bg-gray-100 px-2 py-1 rounded">Configuración → Hardware → Escáner</code></li>
                                    <li>• Habilitar escáner de códigos QR</li>
                                    <li>• Configurar decodificación automática</li>
                                </ol>
                            </div>

                            <div className="bg-white p-3 rounded border">
                                <h5 className="font-medium text-gray-800 mb-2">2. Configurar extracción de datos:</h5>
                                <ul className="space-y-1 text-sm text-gray-600">
                                    <li>• <strong>Campo a extraer:</strong> customer_ref</li>
                                    <li>• <strong>Variable en Agora:</strong> {'{customer_ref_del_qr}'}</li>
                                    <li>• <strong>Incluir en webhook:</strong> CustomerId</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            )
        },
        {
            id: 'testing',
            title: 'Paso 3: Probar la Configuración',
            icon: faTools,
            content: (
                <div className="space-y-6">
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <h4 className="font-semibold text-green-800 mb-3">¿Cómo probar que funciona?</h4>
                        <ol className="space-y-2 text-green-700">
                            <li>1. <strong>Genera un QR de prueba</strong> desde tu panel de PetitPrime</li>
                            <li>2. <strong>Escanea el QR</strong> en tu TPV Agora</li>
                            <li>3. <strong>Procesa una venta</strong> normal</li>
                            <li>4. <strong>Verifica</strong> que los puntos se acumulan en la cuenta del cliente</li>
                        </ol>
                    </div>

                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <h4 className="font-semibold text-blue-800 mb-3">¿Dónde verificar que funciona?</h4>
                        <div className="space-y-3">
                            <div className="bg-white p-3 rounded border">
                                <h5 className="font-medium text-gray-800 mb-2">📱 En la app del cliente:</h5>
                                <p className="text-sm text-gray-600">El cliente recibirá una notificación con sus nuevos puntos</p>
                            </div>

                            <div className="bg-white p-3 rounded border">
                                <h5 className="font-medium text-gray-800 mb-2">💻 En tu panel de PetitPrime:</h5>
                                <p className="text-sm text-gray-600">Puedes ver las estadísticas de uso del TPV en la sección TPV</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                        <h4 className="font-semibold text-yellow-800 mb-2">⚠️ Si algo no funciona</h4>
                        <ul className="space-y-1 text-yellow-700 text-sm">
                            <li>• Verifica que la URL del webhook sea correcta</li>
                            <li>• Asegúrate de que el escáner de QR esté habilitado</li>
                            <li>• Comprueba que la conexión a internet funcione</li>
                            <li>• Contacta con soporte si el problema persiste</li>
                        </ul>
                    </div>
                </div>
            )
        },
        {
            id: 'workflow',
            title: '¿Cómo funciona en la práctica?',
            icon: faRocket,
            content: (
                <div className="space-y-6">
                    <div className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg p-6">
                        <h4 className="font-semibold text-gray-800 mb-4 text-center">Flujo completo: Cliente acumula puntos</h4>

                        <div className="space-y-6">
                            <div className="flex items-start space-x-4">
                                <div className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-sm">1</div>
                                <div className="flex-1">
                                    <h5 className="font-medium text-gray-800 mb-2">Cliente genera QR</h5>
                                    <p className="text-sm text-gray-600">El cliente abre su app PetitPrime y genera un QR para acumular puntos</p>
                                </div>
                            </div>

                            <div className="flex items-start space-x-4">
                                <div className="flex-shrink-0 w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center font-bold text-sm">2</div>
                                <div className="flex-1">
                                    <h5 className="font-medium text-gray-800 mb-2">Cliente en la tienda</h5>
                                    <p className="text-sm text-gray-600">El cliente presenta su QR en el TPV al pagar su compra</p>
                                </div>
                            </div>

                            <div className="flex items-start space-x-4">
                                <div className="flex-shrink-0 w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold text-sm">3</div>
                                <div className="flex-1">
                                    <h5 className="font-medium text-gray-800 mb-2">Agora procesa la venta</h5>
                                    <p className="text-sm text-gray-600">Agora escanea el QR, procesa la venta y envía los datos a PetitPrime</p>
                                </div>
                            </div>

                            <div className="flex items-start space-x-4">
                                <div className="flex-shrink-0 w-8 h-8 bg-orange-600 text-white rounded-full flex items-center justify-center font-bold text-sm">4</div>
                                <div className="flex-1">
                                    <h5 className="font-medium text-gray-800 mb-2">Puntos se acumulan</h5>
                                    <p className="text-sm text-gray-600">PetitPrime recibe los datos y suma los puntos automáticamente a la cuenta del cliente</p>
                                </div>
                            </div>

                            <div className="flex items-start space-x-4">
                                <div className="flex-shrink-0 w-8 h-8 bg-red-600 text-white rounded-full flex items-center justify-center font-bold text-sm">5</div>
                                <div className="flex-1">
                                    <h5 className="font-medium text-gray-800 mb-2">Cliente recibe notificación</h5>
                                    <p className="text-sm text-gray-600">El cliente recibe una notificación en su app con sus nuevos puntos</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )
        },
        {
            id: 'support',
            title: 'Soporte',
            icon: faPhone,
            content: (
                <div className="space-y-6">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                        <h4 className="font-semibold text-blue-800 mb-4 text-center">Contacto Técnico</h4>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-4">
                                <div className="flex items-center space-x-3">
                                    <FontAwesomeIcon icon={faEnvelope} className="text-blue-600" />
                                    <div>
                                        <p className="font-medium text-gray-800">Email</p>
                                        <a href="mailto:soporte@petitprime.com" className="text-blue-600 hover:text-blue-800">
                                            soporte@petitprime.com
                                        </a>
                                    </div>
                                </div>

                                <div className="flex items-center space-x-3">
                                    <FontAwesomeIcon icon={faGlobe} className="text-blue-600" />
                                    <div>
                                        <p className="font-medium text-gray-800">Documentación</p>
                                        <a href="https://docs.petitprime.com" className="text-blue-600 hover:text-blue-800">
                                            https://docs.petitprime.com
                                        </a>
                                    </div>
                                </div>

                                <div className="flex items-center space-x-3">
                                    <FontAwesomeIcon icon={faCode} className="text-blue-600" />
                                    <div>
                                        <p className="font-medium text-gray-800">Issues</p>
                                        <a href="https://github.com/petitprime/issues" className="text-blue-600 hover:text-blue-800">
                                            Crear issue en el repositorio
                                        </a>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-white p-4 rounded border">
                                <h5 className="font-medium text-gray-800 mb-3">Información para Soporte</h5>
                                <ul className="space-y-2 text-sm text-gray-600">
                                    <li>• <strong>Versión de Agora:</strong> [Versión instalada]</li>
                                    <li>• <strong>Logs de error:</strong> [Copiar logs completos]</li>
                                    <li>• <strong>Configuración:</strong> [Screenshot de configuración]</li>
                                    <li>• <strong>Payload enviado:</strong> [Ejemplo del webhook enviado]</li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    <div className="bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-lg p-6 text-center">
                        <h4 className="font-semibold text-gray-800 mb-2">¡Tu sistema TPV está listo para producción! 🎉</h4>
                        <p className="text-gray-600 text-sm">
                            Con esta guía has configurado completamente tu TPV de Agora para acumular puntos automáticamente al escanear QR.
                        </p>
                    </div>
                </div>
            )
        }
    ];

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <div className="bg-white shadow-sm border-b">
                <div className="max-w-7xl mx-auto px-4 py-6">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                            <div className="p-2 bg-blue-100 rounded-lg">
                                <FontAwesomeIcon icon={faQrcode} className="text-blue-600 text-xl" />
                            </div>
                            <div>
                                <h1 className="text-3xl font-bold text-gray-800">Configuración TPV Agora</h1>
                                <p className="text-gray-600">Guía completa para configurar tu Terminal de Punto de Venta con Agora Software</p>
                            </div>
                        </div>
                        <button
                            onClick={toggleAllSections}
                            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                        >
                            <FontAwesomeIcon icon={allExpanded ? faChevronUp : faChevronDown} />
                            <span>{allExpanded ? 'Colapsar Todo' : 'Expandir Todo'}</span>
                        </button>
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="max-w-7xl mx-auto px-4 py-8">
                {/* Quick Navigation */}
                <div className="bg-white rounded-lg shadow border mb-8">
                    <div className="p-6">
                        <h3 className="text-lg font-semibold text-gray-800 mb-4">📋 Índice de Navegación</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                            {sections.map((section, index) => (
                                <button
                                    key={section.id}
                                    onClick={() => {
                                        const element = document.getElementById(`section-${section.id}`);
                                        if (element) {
                                            element.scrollIntoView({ behavior: 'smooth' });
                                        }
                                    }}
                                    className="flex items-center space-x-2 p-3 text-left hover:bg-gray-50 rounded-lg transition-colors border border-gray-200"
                                >
                                    <span className="text-sm font-medium text-gray-500">{index + 1}</span>
                                    <FontAwesomeIcon icon={section.icon} className="text-blue-600 text-sm" />
                                    <span className="text-sm text-gray-700">{section.title}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="space-y-6">
                    {sections.map((section) => (
                        <div key={section.id} id={`section-${section.id}`} className="bg-white rounded-lg shadow border">
                            <button
                                onClick={() => toggleSection(section.id)}
                                className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors rounded-t-lg"
                            >
                                <div className="flex items-center space-x-3">
                                    <FontAwesomeIcon icon={section.icon} className="text-blue-600" />
                                    <h2 className="text-xl font-semibold text-gray-800">{section.title}</h2>
                                </div>
                                <FontAwesomeIcon
                                    icon={expandedSections[section.id] ? faChevronUp : faChevronDown}
                                    className="text-gray-400"
                                />
                            </button>

                            {expandedSections[section.id] && (
                                <div className="px-6 pb-6 border-t border-gray-100 bg-gray-50 rounded-b-lg">
                                    <div className="pt-4">
                                        {section.content}
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>

            {/* Footer */}
            <div className="bg-gray-900 text-white py-8">
                <div className="max-w-7xl mx-auto px-4 text-center">
                    <p className="text-gray-400 text-sm">
                        Documentación generada para PetitPrime TPV Agora Integration v1.0
                    </p>
                    <p className="text-gray-500 text-xs mt-1">
                        Última actualización: {new Date().toLocaleDateString()}
                    </p>
                </div>
            </div>
        </div>
    );
};

export default TpvAgoraDocumentation;
